import { html } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { getProgress } from '../lib/store.js';
import { loadChapter, lesson } from '../lib/content.js';
import { ago } from '../lib/format.js';
import { topbar } from '../components/chrome.js';
import { verseRow } from '../components/verse.js';
import { openNoteEditor } from '../components/actions.js';
import { navigate } from '../lib/router.js';

function bookmarks(progress) {
  return Object.entries(progress.bookmarks)
    .filter(([, b]) => b.on)
    .sort((a, b) => b[1].t - a[1].t)
    .map(([id, b]) => ({ id, t: b.t }));
}

function notes(progress) {
  return Object.entries(progress.notes)
    .filter(([, n]) => n.text && n.text.trim())
    .sort((a, b) => b[1].t - a[1].t)
    .map(([key, n]) => ({ key, ...n }));
}

function exportNotes(list) {
  const lines = ['# My notes on the Bhagavad Gita', ''];
  for (const n of list) {
    const title = n.key.startsWith('r:') ? `Reflection: ${lesson(n.key.slice(2))?.title || n.key}` : `Verse ${n.key}`;
    lines.push(`## ${title}`, `_${new Date(n.t).toLocaleString()}_`, '', n.text, '');
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'gita-notes.md';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default async function saved(params, r) {
  const tab = r.query && r.query.get('tab') === 'notes' ? 'notes' : 'bookmarks';
  const progress = getProgress();
  const bm = bookmarks(progress);
  const ns = notes(progress);
  const chapters = new Map();
  for (const id of new Set(bm.map((b) => Number(b.id.split('.')[0])))) chapters.set(id, await loadChapter(id));

  const bookmarkList = bm.length
    ? html`<div class="verse-rows">${bm.map((b) => {
      const [ch, v] = b.id.split('.').map(Number);
      const verse = chapters.get(ch).verseMap.get(v);
      return verseRow(ch, verse, { sub: `Verse ${b.id} · saved ${ago(b.t)}` });
    })}</div>`
    : html`<div class="empty"><span class="empty__icon">${icon('bookmark', { size: 24 })}</span>
        <p class="empty__title">No bookmarks yet</p>
        <p class="empty__text">Tap the bookmark on any verse to keep it here, ready to come back to.</p>
        <a class="btn btn--secondary" href="/explore#famous">Browse famous verses</a></div>`;

  const noteList = ns.length
    ? html`<ul class="notes">${ns.map((n) => {
      const isReflection = n.key.startsWith('r:');
      const l = isReflection ? lesson(n.key.slice(2)) : null;
      const [ch, v] = isReflection ? [0, 0] : n.key.split('.').map(Number);
      return html`<li><button type="button" class="card note-card" data-open-note="${n.key}" data-href="${isReflection ? '' : `/verse/${ch}/${v}`}">
        <span class="note-card__head"><span class="eyebrow">${isReflection ? 'Reflection' : `Note on verse ${n.key}`}</span><span class="tiny muted">${ago(n.t)}</span></span>
        ${l ? html`<span class="note-card__title">${l.title}</span>` : ''}
        <span class="note-card__text">${n.text}</span>
      </button></li>`;
    })}</ul>
    <button type="button" class="btn btn--secondary" data-export style="margin-top:20px">${icon('download', { size: 18 })} Export notes</button>`
    : html`<div class="empty"><span class="empty__icon">${icon('notes', { size: 24 })}</span>
        <p class="empty__title">No notes yet</p>
        <p class="empty__text">Your reflections at the end of each lesson, and any notes you add to verses, will collect here.</p></div>`;

  return {
    title: 'Saved',
    html: html`<div class="saved">
      ${topbar({ title: 'Saved' })}
      <header class="wrap page-head">
        <h1 class="h1">Saved</h1>
        <p class="lede">Verses you bookmarked and everything you wrote.</p>
        <div class="segmented saved__tabs" role="tablist" aria-label="Saved items">
          <button type="button" role="tab" id="tab-bookmarks" aria-controls="panel" aria-selected="${tab === 'bookmarks' ? 'true' : 'false'}" data-tab="bookmarks">${icon('bookmark', { size: 16 })} Bookmarks <span class="num">${bm.length}</span></button>
          <button type="button" role="tab" id="tab-notes" aria-controls="panel" aria-selected="${tab === 'notes' ? 'true' : 'false'}" data-tab="notes">${icon('notes', { size: 16 })} Notes <span class="num">${ns.length}</span></button>
        </div>
      </header>
      <div class="wrap" id="panel" role="tabpanel" aria-labelledby="tab-${tab}">
        ${tab === 'bookmarks' ? bookmarkList : noteList}
      </div>
    </div>`,
    mount(root) {
      for (const b of root.querySelectorAll('[data-tab]')) {
        b.addEventListener('click', () => navigate(b.dataset.tab === 'notes' ? '/saved?tab=notes' : '/saved', { replace: true }));
      }
      const exp = root.querySelector('[data-export]');
      if (exp) exp.addEventListener('click', () => exportNotes(ns));
      root.addEventListener('click', (e) => {
        const n = e.target.closest('[data-open-note]');
        if (!n) return;
        if (n.dataset.href) navigate(n.dataset.href);
        else {
          const l = lesson(n.dataset.openNote.slice(2));
          openNoteEditor(n.dataset.openNote, { title: 'Your reflection', prompt: l ? l.title : '' });
        }
      });
      const onNote = () => navigate(`/saved${tab === 'notes' ? '?tab=notes' : ''}`, { replace: true });
      document.addEventListener('bg:note', onNote);
      return () => document.removeEventListener('bg:note', onNote);
    },
  };
}
